import { motion } from "framer-motion";
import { useRef, useEffect, useState } from "react";

// —— Fade / slide up when in view ——
export function Reveal({
  children,
  delay = 0,
  y = 40,
  className = "",
  once = true,
  duration = 1,
  as = "div",
}) {
  const MotionTag = motion[as] || motion.div;
  return (
    <MotionTag
      initial={{ opacity: 0, y, filter: "blur(6px)" }}
      whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
      viewport={{ once, margin: "-10% 0px" }}
      transition={{ duration, delay, ease: [0.2, 0.7, 0.2, 1] }}
      className={className}
    >
      {children}
    </MotionTag>
  );
}

// —— Masked line-by-line reveal ——
// Split text into lines; each line clips overflow and slides its span from 100% to 0.
export function MaskedLines({
  lines = [],
  className = "",
  lineClassName = "",
  delay = 0,
  stagger = 0.12,
  duration = 1.05,
}) {
  return (
    <div className={className}>
      {lines.map((line, i) => (
        <span key={i} className={`mask-line ${lineClassName}`}>
          <motion.span
            initial={{ y: "110%" }}
            animate={{ y: "0%" }}
            transition={{
              duration,
              delay: delay + i * stagger,
              ease: [0.2, 0.7, 0.2, 1],
            }}
          >
            {line}
          </motion.span>
        </span>
      ))}
    </div>
  );
}

// —— Counter (visible when in view) ——
export function Counter({ to = 100, suffix = "", duration = 1.6 }) {
  const ref = useRef(null);
  const [val, setVal] = useState(0);
  useEffect(() => {
    let obs;
    const el = ref.current;
    if (!el) return;
    obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            const start = performance.now();
            const step = (now) => {
              const p = Math.min((now - start) / (duration * 1000), 1);
              setVal(Math.round(to * (1 - Math.pow(1 - p, 3))));
              if (p < 1) requestAnimationFrame(step);
            };
            requestAnimationFrame(step);
            obs.disconnect();
          }
        });
      },
      { threshold: 0.4 }
    );
    obs.observe(el);
    return () => obs && obs.disconnect();
  }, [to, duration]);
  return (
    <span ref={ref}>
      {val}
      {suffix}
    </span>
  );
}
